import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, combineLatest, distinctUntilChanged, filter } from 'rxjs';
import { collections } from 'src/app/core/constants/collections';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { selectDefaultReportTypeId, selectUser } from 'src/app/core/store/selectors';
import { AppState } from 'src/app/core/store/state';
import { markAsNotRequired, markAsRequired } from 'src/app/core/utils/form-controls.utils';
import { isPresent } from 'src/app/core/utils/isPresent';
import { emailListValidator } from 'src/app/core/validators/email-list-validator';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { AlertType } from 'src/app/shared/components/app-alert/app-alert.component';
import {
  ReportFormat,
  SendReportDto,
  ReportContent,
} from 'src/app/shared/services/reporting-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { GetDocumentDto } from '../../../models/documents.model';
import { DocumentsActions } from '../../../store/actions';
import { fromDocuments } from '../../../store/selectors';

@UntilDestroy()
@Component({
  selector: 'app-doc-send-report-modal',
  templateUrl: './doc-send-report-modal.component.html',
  styleUrl: './doc-send-report-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocSendReportModalComponent implements OnInit, AfterViewInit {
  @Input() document!: GetDocumentDto;
  @Input() clauseVarianceMode: boolean = false;

  public onClose: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup | undefined;
  public title: string = this.translocoService.translate(`titles.sendReport`);
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public alertType = AlertType;
  public currentUser: UserDto | null | undefined;
  public sending: boolean = false;

  private sendToMeFormControl!: FormControl<any>;
  private sendToOthersFormControl!: FormControl<any>;
  private sendToOthersEmailsFormControl!: FormControl<any>;
  private reportFormatFormControl!: FormControl<any>;
  public reportFormats = collections.reportFormats;
  public canWrite = false;

  constructor(
    private store$: Store<AppState>,
    private bsModalRef: BsModalRef,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly permissionService: PermissionService,
  ) {}

  ngAfterViewInit(): void {
    this.store$
      .select(fromDocuments.selectCloseSendingReportModal)
      .pipe(untilDestroyed(this))
      .subscribe((close) => {
        if (close) {
          this.bsModalRef.hide();
          this.store$.dispatch(DocumentsActions.clearSendReportCancelled());
        }
      });

    this.store$
      .select(fromDocuments.selectSendingReport)
      .pipe(untilDestroyed(this))
      .subscribe((sending) => {
        this.sending = sending;
      });
  }

  public ngOnInit(): void {
    this.initForm();
    this.permissionService
      .canWrite$(PermissionResource.Reporting)
      .pipe(untilDestroyed(this))
      .subscribe((canWrite) => {
        this.canWrite = canWrite;
        this.syncFormAccess();
      });
  }

  initControls() {
    this.sendToMeFormControl = this.form?.get('sendToMe') as FormControl;
    this.sendToOthersFormControl = this.form?.get('sendToOthers') as FormControl;
    this.sendToOthersEmailsFormControl = this.form?.get('sendToOthersEmails') as FormControl;
    this.reportFormatFormControl = this.form?.get('reportFormat') as FormControl;
  }

  initSubscription() {
    this.sendToOthersFormControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((value) => {
        this.onSendToOthersChange(value);
      });
  }

  initForm() {
    combineLatest([this.store$.select(selectUser), this.store$.select(selectDefaultReportTypeId)])
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(([user, defaultReportTypeId]) => {
        this.currentUser = user;

        setTimeout(() => {
          this.form = this.fb.group(
            {
              sendToMe: [true, { validators: [] }],
              reportContentSummary: [true, { validators: [] }],
              reportContentPlaybookCompliance: [true, { validators: [] }],
              reportContentKeyQuestions: [true, { validators: [] }],
              reportContentIntegrityCheck: [false, { validators: [] }],
              reportContentRegulatoryCompliance: [false, { validators: [] }],
              reportContentReadabilityAnalysis: [false, { validators: [] }],
              sendToOthers: [false, { validators: [] }],
              sendToOthersEmails: ['', { validators: [emailListValidator] }],
              reportFormat: [defaultReportTypeId || ReportFormat.PDF, { validators: [] }],
            },
            { validators: [this.reportContentValidator.bind(this)] },
          );

          this.initControls();
          this.initSubscription();
          this.syncFormAccess();
          this.cdr.markForCheck();
        });
      });
  }

  onSendToOthersChange(value: boolean): void {
    if (value === true) {
      markAsRequired(this.sendToOthersEmailsFormControl);
    } else {
      this.sendToOthersEmailsFormControl.patchValue('');
      markAsNotRequired(this.sendToOthersEmailsFormControl);
    }

    this.cdr.markForCheck();
  }

  get isSendToOthers(): boolean {
    return this.sendToOthersFormControl.value === true;
  }

  get isSendToOthersEmailsInvalid(): boolean {
    return false;
  }

  get isFormValid(): boolean {
    if (this.form?.invalid) {
      return false;
    }

    return this.sendToMeFormControl.value === true || this.sendToOthersFormControl.value === true;
  }

  public onConfirm(): void {
    if (!this.canWrite) {
      return;
    }

    let documentId = this.document?.uniqueId;
    let recipients = [];
    let reportFormatId = this.reportFormatFormControl.value;
    let reportContents = this.getReportContents();

    if (this.sendToMeFormControl.value === true) {
      recipients.push(this.currentUser?.email);
    }

    if (this.sendToOthersFormControl.value === true) {
      recipients = recipients.concat(
        this.sendToOthersEmailsFormControl.value.split(',')?.map((email: string) => email.trim()),
      );
    }

    let report = {
      documentId,
      recipients,
    } as SendReportDto;

    if (!this.clauseVarianceMode) {
      report = { ...report, reportFormatId, reportContents };
    }

    // todo: test when api will be fixed
    this.store$.dispatch(DocumentsActions.sendReport({ report, doc: this.document }));
  }

  public onCancel(): void {
    const pristine = !!this.form?.pristine;
    this.store$.dispatch(DocumentsActions.sendReportCancel({ pristine }));
  }

  public reportContentValidator(control: AbstractControl): ValidationErrors | null {
    const group = control as FormGroup;
    const keys = [
      'reportContentSummary',
      'reportContentPlaybookCompliance',
      'reportContentKeyQuestions',
      'reportContentIntegrityCheck',
      'reportContentRegulatoryCompliance',
      'reportContentReadabilityAnalysis',
    ];

    const anySelected = keys.some((k) => !!group.get(k)?.value);
    return anySelected ? null : { reportContentRequired: true };
  }

  public getReportContents(): ReportContent[] {
    const contents: ReportContent[] = [];

    if (this.form?.get('reportContentSummary')?.value) {
      contents.push(ReportContent.Summary);
    }

    if (this.form?.get('reportContentPlaybookCompliance')?.value) {
      contents.push(ReportContent.PlaybookCompliance);
    }

    if (this.form?.get('reportContentKeyQuestions')?.value) {
      contents.push(ReportContent.KeyQuestions);
    }

    if (this.form?.get('reportContentIntegrityCheck')?.value) {
      contents.push(ReportContent.IntegrityCheck);
    }

    if (this.form?.get('reportContentRegulatoryCompliance')?.value) {
      contents.push(ReportContent.RegulatoryCompliance);
    }

    if (this.form?.get('reportContentReadabilityAnalysis')?.value) {
      contents.push(ReportContent.ReadabilityAnalysis);
    }

    return contents;
  }

  private syncFormAccess(): void {
    if (!this.form) {
      return;
    }

    if (this.canWrite) {
      this.form.enable({ emitEvent: false });
      return;
    }

    this.form.disable({ emitEvent: false });
  }
}
